import React from 'react';
import {Checkbox, FormControlLabel} from '@mui/material';
import CustomCheckboxIcon from './CustomCheckboxIcon';
import './ArrCheckbox.scss';

export interface CheckboxProps {
  variant?: 'primary' | 'secondary';
  size?: 'xs' | 'small' | 'medium' | 'large';
  disabled?: boolean;
  checked: boolean;
  className?: string;
  onChange?: (checked: boolean) => void;
  label?: string;
  labelText?: string | React.ReactNode; //visible label next to checkbox
  labelPlacement?: 'start' | 'end'; //controls label position
  icon?: React.ReactNode;
  iconPosition?: 'left' | 'right';
  fullWidth?: boolean;
  shape?: boolean;
  'aria-label': string;
  name?: string;
  id?: string;
  isAllSelected?: boolean;
  isIndeterminate?: boolean;
  borderRadius?: number;
  onClick?: (e: any) => void;
  onKeyDown?: (e: any) => void;
  tabIndex?: number;
  inputProps?: React.InputHTMLAttributes<HTMLInputElement>;
  disableRipple?: boolean;
}

const iconSize = {
  large: 1.5,
  medium: 1.25,
  small: 1.125,
  xs: 1,
};

const ArrCheckbox: React.FC<CheckboxProps> = ({
  size = 'large',
  onChange,
  isAllSelected = false,
  className,
  checked = false,
  isIndeterminate = false,
  disabled = false,
  label = '',
  labelText = '', //default empty string
  labelPlacement = 'end', // default label on right
  borderRadius = 2,
  onKeyDown,
  'aria-label': ariaLabel,
  ...props
}) => {
  const handleChange = (
    event: React.ChangeEvent<HTMLInputElement>,
    checked: boolean
  ) => {
    if (!disabled) onChange?.(checked);
  };

  const handleKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === 'Enter' && onChange && !disabled) {
      event.preventDefault();
      event.stopPropagation();
      const newChecked = !checked;
      onChange(newChecked);
    }
    onKeyDown?.(event);
  };

  const checkbox = (
    <Checkbox
      data-testid={ariaLabel}
      className="arr-checkbox"
      slotProps={{
        input: {
          'aria-label': ariaLabel,
          ...({'data-testid': `${ariaLabel}-input`} as any),
          ...props.inputProps,
        },
      }}
      checked={checked}
      disabled={disabled}
      value={label}
      onChange={handleChange}
      onKeyDown={handleKeyDown}
      indeterminate={isIndeterminate}
      icon={
        <CustomCheckboxIcon
          isChecked={checked}
          iconSize={iconSize[size]}
          isIndeterminate={isIndeterminate}
          isAllSelected={isAllSelected}
          isDisabled={disabled}
          borderRadius={borderRadius}
          dataTestId={`${ariaLabel}-icon`}
        />
      }
      checkedIcon={
        <CustomCheckboxIcon
          isChecked={checked}
          iconSize={iconSize[size]}
          isIndeterminate={isIndeterminate}
          isAllSelected={isAllSelected}
          isDisabled={disabled}
          borderRadius={borderRadius}
          dataTestId={`${ariaLabel}-checked-icon`}
        />
      }
      indeterminateIcon={
        <CustomCheckboxIcon
          isChecked={true}
          iconSize={iconSize[size]}
          isIndeterminate={true}
          isAllSelected={false}
          isDisabled={disabled}
          borderRadius={borderRadius}
          dataTestId={`${ariaLabel}-indeterminate-icon`}
        />
      }
      {...props}
    />
  );

  //  If labelText is provided, wrap with FormControlLabel
  return labelText ? (
    <FormControlLabel
      data-testid={`checkbox-${String(labelText)
        .replace(/\s+/g, '-')
        .toLowerCase()}`}
      // width is required or else the whole block is clickable
      sx={{width: 'fit-content'}}
      className={className}
      control={checkbox}
      label={labelText}
      labelPlacement={labelPlacement}
      slotProps={{
        typography: {
          // Cast to any to allow custom data-attributes
          ...({
            'data-testid': `label-${String(labelText)?.replaceAll(' ', '-') ?? ''}`,
          } as any),
        },
      }}
    />
  ) : (
    checkbox
  );
};

export default ArrCheckbox;
